import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { ConversationTemplateSchema, type ConversationTemplate } from "@samklang/schemas";
import { z } from "zod";
import {
  HistoryListSchema,
  HistoryDetailSchema,
  ActiveFollowUpActionsSchema,
  MetricHistorySchema,
  SaveApprovedSessionInputSchema,
  type HistoryItem,
  type ActiveFollowUpAction,
  type HistoryDetail,
  type MetricPoint,
  type SamklangDatabase,
  type SaveApprovedSessionInput,
} from "./models";

export const PublicDatabaseEnvironmentSchema = z.object({
  VITE_SUPABASE_URL: z.string().url(),
  VITE_SUPABASE_PUBLISHABLE_KEY: z.string().startsWith("sb_publishable_"),
});
/** Backwards-compatible camel-case export used by the public environment contract. */
export const publicDatabaseEnvironmentSchema = PublicDatabaseEnvironmentSchema;
export type PublicDatabaseEnvironment = z.infer<typeof PublicDatabaseEnvironmentSchema>;
export type SamklangClient = SupabaseClient<SamklangDatabase>;

export const PasswordSignInSchema = z.object({
  email: z.string().email(),
  // Login must accept accounts created before the current 12-character
  // password policy. The stricter policy belongs to password creation and
  // reset, not to authentication of an existing account.
  password: z.string().min(1).max(128),
});
export type PasswordSignIn = z.infer<typeof PasswordSignInSchema>;

export function createSamklangClient(environment: PublicDatabaseEnvironment): SamklangClient {
  const parsed = PublicDatabaseEnvironmentSchema.parse(environment);
  return createClient<SamklangDatabase>(parsed.VITE_SUPABASE_URL, parsed.VITE_SUPABASE_PUBLISHABLE_KEY, { auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true } });
}

export async function signInWithPassword(client: SamklangClient, credentials: PasswordSignIn): Promise<void> {
  const parsed = PasswordSignInSchema.parse(credentials);
  const { error } = await client.auth.signInWithPassword(parsed);
  if (error) throw new Error(error.message);
}

export async function verifyEmailOtp(client: SamklangClient, email: string, token: string): Promise<void> {
  const { error } = await client.auth.verifyOtp({ email: z.string().email().parse(email), token: z.string().regex(/^\d{6}$/).parse(token), type: "email" });
  if (error) throw new Error(error.message);
}

export async function listPublishedConversationTemplates(client: SamklangClient): Promise<readonly ConversationTemplate[]> {
  const { data, error } = await client
    .from("conversation_template_versions")
    .select("template_id,version,content")
    .eq("status", "published")
    .order("template_id", { ascending: true })
    .order("version", { ascending: false });
  if (error) throw new Error(error.message);
  const rows = z.array(z.object({ template_id: z.string(), version: z.number().int().positive(), content: z.unknown() })).parse(data ?? []);
  const seen = new Set<string>();
  const templates: ConversationTemplate[] = [];
  for (const row of rows) {
    if (seen.has(row.template_id)) continue;
    const parsed = ConversationTemplateSchema.safeParse(row.content);
    if (!parsed.success || parsed.data.id !== row.template_id || parsed.data.version !== row.version) continue;
    seen.add(row.template_id);
    templates.push(parsed.data);
  }
  return templates;
}

export async function listHistory(client: SamklangClient): Promise<readonly HistoryItem[]> {
  const { data, error } = await client.from("history_items").select("id,occurred_at,template_title,pseudonym,themes,has_follow_up,expires_at").order("occurred_at", { ascending: false });
  if (error) throw new Error(error.message);
  return HistoryListSchema.parse(data);
}

export async function listMetricHistory(client: SamklangClient): Promise<readonly MetricPoint[]> {
  const { data, error } = await client.from("metric_history").select("session_id,pseudonym,occurred_at,metric_key,metric_label,metric_value,metric_direction").order("occurred_at", { ascending: true });
  if (error) throw new Error(error.message);
  return MetricHistorySchema.parse(data ?? []);
}

export async function listActiveFollowUpActions(client: SamklangClient, participantReference: string): Promise<readonly ActiveFollowUpAction[]> {
  const pseudonym = z.string().trim().min(1).max(80).parse(participantReference);
  const { data, error } = await client.from("active_follow_up_actions").select("id,session_id,pseudonym,description,responsible,start_date,follow_up_date,hoped_outcome,success_indicator,status,visibility").eq("pseudonym", pseudonym).order("follow_up_date", { ascending: true, nullsFirst: false });
  if (error) throw new Error(error.message);
  return ActiveFollowUpActionsSchema.parse(data ?? []);
}

export async function deleteSession(client: SamklangClient, sessionId: string): Promise<void> {
  const id = z.string().uuid().parse(sessionId);
  const { error } = await client.from("sessions").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

export async function getHistoryDetail(client: SamklangClient, sessionId: string): Promise<HistoryDetail> {
  const id = z.string().uuid().parse(sessionId);
  const [{ data: summary, error: summaryError }, { data: actions, error: actionsError }] = await Promise.all([
    client.from("session_summaries").select("version,summary,approved_at").eq("session_id", id).order("version", { ascending: false }).limit(1).maybeSingle(),
    client.from("follow_up_actions").select("id,description,responsible,start_date,follow_up_date,hoped_outcome,success_indicator,status,visibility").eq("session_id", id).order("created_at", { ascending: true }),
  ]);
  if (summaryError) throw new Error(summaryError.message);
  if (actionsError) throw new Error(actionsError.message);
  if (!summary) throw new Error("Sammanfattningen finns inte eller är inte tillgänglig.");
  return HistoryDetailSchema.parse({
    version: summary.version,
    approvedAt: summary.approved_at,
    items: summary.summary,
    actions: actions?.map((action) => ({
      id: action.id,
      description: action.description,
      responsible: action.responsible,
      startDate: action.start_date,
      followUpDate: action.follow_up_date,
      hopedOutcome: action.hoped_outcome,
      successIndicator: action.success_indicator,
      status: action.status,
      visibility: action.visibility,
    })) ?? [],
  });
}

export async function saveApprovedSession(
  client: SamklangClient,
  input: SaveApprovedSessionInput,
): Promise<string> {
  const parsed = SaveApprovedSessionInputSchema.parse(input);
  const { data: authData, error: authError } = await client.auth.getUser();
  if (authError || !authData.user) throw new Error("Du måste vara inloggad för att spara.");

  const { data: membership, error: membershipError } = await client
    .from("organization_members")
    .select("organization_id")
    .eq("user_id", authData.user.id)
    .limit(1)
    .maybeSingle();
  if (membershipError) throw new Error(membershipError.message);
  if (!membership) throw new Error("Kontot tillhör ingen organisation.");

  const { data: organization, error: organizationError } = await client
    .from("organizations")
    .select("retention_days")
    .eq("id", membership.organization_id)
    .single();
  if (organizationError) throw new Error(organizationError.message);

  const occurredAt = new Date();
  const expiresAt = new Date(occurredAt);
  expiresAt.setUTCDate(expiresAt.getUTCDate() + organization.retention_days);
  const { data: session, error: sessionError } = await client
    .from("sessions")
    .insert({
      organization_id: membership.organization_id,
      created_by: authData.user.id,
      template_id: parsed.templateId,
      template_version: parsed.templateVersion,
      storage_mode: parsed.pseudonym ? "pseudonymous" : "summary",
      status: "completed",
      pseudonym: parsed.pseudonym,
      themes: parsed.themes,
      occurred_at: occurredAt.toISOString(),
      expires_at: expiresAt.toISOString(),
    })
    .select("id")
    .single();
  if (sessionError) throw new Error(sessionError.message);

  const rollback = async (message: string): Promise<never> => {
    await client.from("sessions").delete().eq("id", session.id);
    throw new Error(message);
  };
  const { error: summaryError } = await client.from("session_summaries").insert({
    session_id: session.id,
    organization_id: membership.organization_id,
    created_by: authData.user.id,
    version: 1,
    summary: parsed.items,
    approved_at: new Date().toISOString(),
  });
  if (summaryError) return rollback(summaryError.message);

  if (parsed.actions.length) {
    const { error: actionsError } = await client.from("follow_up_actions").insert(parsed.actions.map((action) => ({
      session_id: session.id,
      organization_id: membership.organization_id,
      created_by: authData.user.id,
      description: action.description,
      responsible: action.responsible || null,
      start_date: action.startDate || null,
      follow_up_date: action.followUpDate || null,
      hoped_outcome: action.hopedOutcome || null,
      success_indicator: action.successIndicator || null,
      status: action.status,
      visibility: action.visibility,
    })));
    if (actionsError) return rollback(actionsError.message);
  }
  return session.id;
}
